import { useVuexGetters } from '@scfe-common/micro-frontend-utils';
import { ColumnChart, LineChart } from '@ssc-ui/charts-react';
import * as React from 'react';
import { ProFilter } from 'react-pro-components';
import { Form, message, Radio, Skeleton } from 'ssc-ui-react';
import { IconHelpOutline } from 'ssc-ui-react/es/components/icon';

import { $gt } from '@/common/appCtx';
import { ExceptionIconStr, GrowthRateDownIconStr, GrowthRateUpIconStr } from '@/pages/workstation-dashboard/assets/svg/echarts';
import { CacheType, MAX_SELECTED_WORKSTATION, OnTargetRateTooltip, WorkstationPerformanceType } from '@/pages/workstation-dashboard/const';
import { WorkstationOptionItem } from '@/pages/workstation-dashboard/defined';
import useDropdownListStore from '@/pages/workstation-dashboard/store/use-dropdown-list';
import useLastTenHoursWorkstationPerformanceStore from '@/pages/workstation-dashboard/store/use-last-ten-hours-workstation-performance';
import useWorkstationOverviewStore from '@/pages/workstation-dashboard/store/use-workstation-overview';
import { getTimeInterval, transformNum } from '@/pages/workstation-dashboard/utils/common';
import { dashboardDBManager } from '@/pages/workstation-dashboard/utils/storage';

import styles from './index.module.less';

type FormType = {
  biz_workstation_id_list?: string[];
  chartType?: WorkstationPerformanceType;
}

type StorageType = {
  biz_workstation_list?: WorkstationOptionItem[];
  chartType?: WorkstationPerformanceType;
}

const DEFAULT_INTERVAL = '3 p.m. - 4 p.m.';
const DEFAULT_INTERVAL_THROUGH = 0;

const WorkstationPerformanceLastTenHours: React.FC = () => {
  const [form] = Form.useForm();
  const [type, setType] = React.useState<WorkstationPerformanceType>(WorkstationPerformanceType.Throughput);
  const targetGroup = useWorkstationOverviewStore.use.targetGroup();
  const [showSelectedWorkstation, setShowSelectedWorkstation] = React.useState<string[]>([]);
  const [selectedWorkstation, setSelectedWorkstation] = React.useState<string[]>([]);
  const workstationDropdownList = useDropdownListStore.use.lastTenHoursWorkstationDropdownList();
  const setWorkstationDropdownList = useDropdownListStore.use.setLastTenHoursWorkstationDropdownList();
  const workstationDropdownListMap = useDropdownListStore.use.lastTenHoursWorkstationDropdownListMap();
  const workstationDropdownListLoading = useDropdownListStore.use.lastTenHoursWorkstationDropdownListLoading();
  const setForm = useLastTenHoursWorkstationPerformanceStore.use.setForm();
  const loading = useLastTenHoursWorkstationPerformanceStore.use.loading();
  const throughputItems = useLastTenHoursWorkstationPerformanceStore.use.throughputItems();
  const targetItems = useLastTenHoursWorkstationPerformanceStore.use.targetItems();
  const getWorkstationPerformanceLastTenHoursAsync = useLastTenHoursWorkstationPerformanceStore.use.getWorkstationPerformanceLastTenHoursAsync();
  const timeIntervals = useWorkstationOverviewStore.use.timeIntervals();
  const workstationDashboardMockBeginTime = useVuexGetters('workforceGlobalDataOps/workstationDashboardMockBeginTime');

  const setCache = React.useCallback((values: FormType) => {
    const cache: StorageType = {
      biz_workstation_list: values.biz_workstation_id_list?.map(value => ({
        value,
        label: workstationDropdownListMap[value] || '',
      })),
      chartType: values.chartType ?? type,
    };
    dashboardDBManager.put(CacheType.WorkstationPerformanceLast10Hours, cache);
  }, [type, workstationDropdownListMap]);

  const getCache = React.useCallback(async () => {
    try {
      const cache = await dashboardDBManager.get(CacheType.WorkstationPerformanceLast10Hours) as StorageType | undefined;
      const workstationList = cache?.biz_workstation_list || [];
      const bizWorkstationIdList: string[] = [];
      const disabledWorkstationList: WorkstationOptionItem[] = [];
      workstationList.forEach(item => {
        if (!workstationDropdownListMap[item.value]) {
          disabledWorkstationList.push({ ...item, disabled: true });
        }
        bizWorkstationIdList.push(item.value);
      });
      setWorkstationDropdownList(disabledWorkstationList, true);
      form.setFieldValue('biz_workstation_id_list', bizWorkstationIdList.length ? bizWorkstationIdList : undefined);
      setType(cache?.chartType || WorkstationPerformanceType.Throughput);
    } catch (error) {
      console.error('[WorkstationPerformanceLastTenHours] getCache error: ', error);
    } finally {
      setForm(form);
    }
  }, [setWorkstationDropdownList, form, workstationDropdownListMap, setType, setForm]);

  React.useLayoutEffect(() => {
    getCache();
  }, [getCache]);

  React.useEffect(() => {
    if (!targetGroup || !targetGroup.groupId) {
      setSelectedWorkstation(prev => prev.filter(item => item !== targetGroup.workstation_group));
    } else {
      setSelectedWorkstation(prev => {
        if (!targetGroup.workstation_group || prev.includes(targetGroup.workstation_group)) return prev;
        return [targetGroup.workstation_group, ...prev];
      });
    }
  }, [setSelectedWorkstation, targetGroup]);

  React.useEffect(() => {
    if (!selectedWorkstation?.length) {
      setShowSelectedWorkstation([]);
      return;
    }
    setShowSelectedWorkstation(prev => {
      if (prev.length === MAX_SELECTED_WORKSTATION) return prev;
      return [
        ...prev,
        ...selectedWorkstation.filter(st => !prev.includes(st)),
      ].filter(Boolean).slice(0, MAX_SELECTED_WORKSTATION);
    });
  }, [selectedWorkstation]);

  const columnChartData = React.useMemo(() => {
    const datasource = throughputItems?.map((item) => {
      const timeBegin = item.time_begin ?? 0;
      const time = getTimeInterval(timeBegin, timeIntervals, workstationDashboardMockBeginTime) ?? '';
      if (!time) return undefined;
      return {
        time,
        throughput: item.throughput ?? 0,
        target: item.target ?? 0,
        workstation: item.workstation ?? '',
      };
    });
    timeIntervals.forEach(interval => {
      const exists = datasource.some(data => data?.time === interval);
      if (!exists) {
        datasource.push({
          time: interval,
          throughput: 0,
          target: 0,
          workstation: targetGroup?.workstation_group || '',
        });
      }
    });
    selectedWorkstation.forEach(workstation => {
      const exists = datasource.some(data => data?.workstation === workstation);
      if (!exists) {
        datasource.push({
          time: DEFAULT_INTERVAL,
          throughput: DEFAULT_INTERVAL_THROUGH,
          target: 0,
          workstation,
        });
      }
    });
    return datasource.filter(Boolean);
  }, [selectedWorkstation, targetGroup?.workstation_group, throughputItems, timeIntervals, workstationDashboardMockBeginTime]);

  const renderColumnChart = () => {
    return (
      <>
        {loading ? (
          <Skeleton.Chart />
        ) : (
          <ColumnChart
            dataSource={columnChartData}
            echartsOption={{
              yAxis: [
                {
                  splitLine: {
                    lineStyle: { type: 'dashed' }
                  },
                  min: 0,
                  max: throughputItems?.length ? undefined : 5000,
                  axisLabel: {
                    formatter: (value: number) => transformNum(value)
                  }
                }
              ],
              xAxis: {
                axisTick: {
                  show: false
                }
              },
              tooltip: {
                show: !!throughputItems?.length,
                formatter: (params) => {
                  if (!Array.isArray(params) || !params.length) return '';
                  const map = columnChartData.reduce((acc, item) => {
                    if (!item) return acc;
                    acc[`${item.time}-${item.workstation}`] = item;
                    return acc;
                  }, {} as Record<string, { time: string; throughput: number; target: number; workstation: string }>);
                  const isAllEmpty = params.every(item => {
                    const key = `${(item as Record<string, any>).axisValue}-${item.seriesName}`;
                    return map[key]?.throughput === 0;
                  });
                  if (isAllEmpty) return '';
                  const title = params[0].name;
                  const axisValue = (params[0] as Record<string, any>).axisValue;
                  const head = `
                    <div>
                      <div style="font-weight: bold; font-size: 16px;">
                        ${title}
                      </div>
                      <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 12px; font-size: 14px; color: #7E8692;">
                        <span>${$gt('Workstation')}</span>
                        <span>${$gt('Throughtput/Target')}</span>
                      </div>
                    </div>
                  `;
                  const content = params.map((item, index) => {
                    const { throughput, target } = map[`${axisValue}-${item.seriesName}`] || {};
                    const shouldWarning = (throughput ?? 0) < (target ?? 0);
                    const normalVal = `${transformNum(throughput)}/${transformNum(target)}`;
                    const warningVal = `<span style="color: #EE4D2D;font-weight: bold;">${normalVal} ${ExceptionIconStr}</span>`;
                    if (throughput === DEFAULT_INTERVAL_THROUGH) return '';
                    return `
                      <div style="display: flex; align-items: baseline; justify-content: space-between; margin-top: 12px; font-size: 14px;">
                        ${item.marker}
                        <div style="width: 115px; margin-right: 12px; color: #7E8692; white-space: normal; word-break: break-all;">
                          ${item.seriesName}
                        </div>
                        <div style="width: 120px; color: #333; white-space: normal; word-break: break-all;">
                          ${shouldWarning ? warningVal : normalVal}
                        </div>
                      </div>
                    `;
                  }).join('');
                  return `
                    ${head}
                    <div style="width: 280px;">
                      ${content}
                    </div>
                  `;
                }
              },
            }}
            config={{
              xField: 'time',
              yField: 'throughput',
              seriesField: 'workstation',
              legend: {
                type: 'checkbox',
                show: true,
                selected: showSelectedWorkstation,
                filter: item => item.name !== 'default',
                onChange: (selectedKeys: string[]) => {
                  if (selectedKeys.length > MAX_SELECTED_WORKSTATION) {
                    message.error($gt('Display limit reached (max 9 bars). Please deselect some items and try again.'));
                    return;
                  }
                  setShowSelectedWorkstation(selectedKeys);
                },
              },
            }}
          />
        )}
      </>
    );
  };

  const lineChartData = React.useMemo(() => {
    if (!targetItems?.length) {
      return timeIntervals.map(time => ({ time, value: 0, compareRate: 0 }));
    }
    return targetItems?.map((item) => {
      const timeBegin = (item.time_begin ?? 0);
      const time = getTimeInterval(timeBegin, timeIntervals, workstationDashboardMockBeginTime) ?? '';
      return {
        time,
        value: item.on_target_rate ?? 0,
        compareRate: item.last_hour_compare_rate ?? 0,
      };
    }, [] as any[]) || [];
  }, [targetItems, timeIntervals, workstationDashboardMockBeginTime]);

  const renderLineChart = () => {
    return (
      <>
        {loading ? (
          <Skeleton.Chart />
        ) : (
          <LineChart
            dataSource={lineChartData}
            echartsOption={{
              tooltip: {
                show: true,
                formatter: (params) => {
                  if (!Array.isArray(params) || !params.length) {
                    return '';
                  }
                  const dataIndex = params[0].dataIndex;
                  const { compareRate, value } = lineChartData[dataIndex] || {};
                  const title = params[0].name;
                  const head = `
                    <div style="font-weight: 500; font-size: 16px; color: #303844;">
                      ${title}
                    </div>
                  `;
                  const compareRateEl = compareRate >= 0 ? `
                    <div style="display: flex; align-items: center; color: #07A880;">
                      <span style="margin-top: -2px; margin-right: 4px;">${GrowthRateUpIconStr}</span>
                      <span>${compareRate}%</span>
                    </div>
                  ` : `
                    <div style="display: flex; align-items: center; color: #F22738;">
                      <span style="margin-top: -2px; margin-right: 4px;">${GrowthRateDownIconStr}</span>
                      <span>${compareRate}%</span>
                    </div>
                  `;
                  const content = `
                    <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 12px; font-size: 14px; color: #7E8692;">
                      ${params[0].marker}
                      <span style="margin-right: 16px;">${$gt('On Target Rate')}</span>
                      <span style="margin-right: 8px;color: #303844; font-weight: 500; font-size: 14px;">${value}%</span>
                      ${compareRateEl}
                    </div>
                  `;
                  return `
                    ${head}
                    ${content}
                  `;
                }
              },
              grid: {
                containLabel: true,
                left: '50px',
                right: '3%',
                bottom: '3%',
              },
              color: ['#FF7438'],
              xAxis: {
                type: 'category',
                axisTick: { show: false },
                data: timeIntervals,
                axisLabel: {
                  formatter: (value: string) => value ? `${value}` : '',
                }
              },
              yAxis: {
                show: true,
                axisTick: { show: false },
                type: 'value',
                axisLabel: {
                  formatter: (value: number) => value ? `${value}%` : '',
                }
              },
              series: [
                {
                  type: 'line',
                  data: lineChartData,
                }
              ],
              legend: { show: false },
            }}
            config={{
              xField: 'time',
              yField: ['value'],
              xAxis: {
                type: 'category',
                labelInterval: 0,
              },
              legend: { show: false },
              showSymbolOnHover: true,
            }}
          />
        )}
      </>
    );
  };

  const handleChartTypeChange = (e: any) => {
    const type = e.target.value as WorkstationPerformanceType;
    setType(type);
    setCache({
      chartType: type,
      biz_workstation_id_list: form.getFieldValue('biz_workstation_id_list'),
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.head}>
        {$gt('Workstation Performance')}
      </div>
      <div className={styles.filter}>
        <Radio.Group
          value={type}
          onChange={handleChartTypeChange}
        >
          <Radio.Button value={WorkstationPerformanceType.Throughput}>
            {$gt('Throughput')}
          </Radio.Button>
          <Radio.Button
            value={WorkstationPerformanceType.OnTargetRate}
            icon={<IconHelpOutline />}
            hint={OnTargetRateTooltip}
          >
            {$gt('On Target Rate')}
          </Radio.Button>
        </Radio.Group>
        <ProFilter
          formProps={{ form }}
          style={{ marginLeft: 8 }}
          fields={[
            {
              label: $gt('Workstation ID/Name'),
              type: 'select',
              name: 'biz_workstation_id_list',
              style: { width: 400 },
              ctrlProps: {
                mode: 'multiple',
                disabled: type === WorkstationPerformanceType.OnTargetRate,
                maxTagCount: 1,
                maxTagTextWidth: 80,
                filterOption: true,
                allowClear: true,
                loading: workstationDropdownListLoading,
                options: workstationDropdownList,
                style: { width: 500 }
              },
            },
          ]}
          onChange={model => {
            if (!targetGroup) return;
            setCache(model as FormType);
            getWorkstationPerformanceLastTenHoursAsync({ workstation_group_id: targetGroup?.groupId, });
          }}
        />
      </div>
      <div className={styles.chartWrapper}>
        {type === WorkstationPerformanceType.Throughput ? renderColumnChart(): renderLineChart()}
      </div>
    </div>
  );
};

export default WorkstationPerformanceLastTenHours;
